import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import "./styles.scss";
import "./testStyles.css";

import {
    ListCrossMinimalistic,
    Mirror,
    Eraser,
    CloseCircle,
    Lock,
    MenuDots,
    ChatDots,
    Tuning,
    AltArrowUp,
    AltArrowDown,
    SidebarMinimalistic,
    Cart2,
} from "@solar-icons/react/ssr";
import {
    Button,
    Divider,
    Grid,
    useTheme,
    CircularProgress,
    useMediaQuery,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { ReactGrid } from "@silevis/reactgrid";
import { useDispatch, useSelector } from "react-redux";

import { useParams } from "react-router-dom";
import { request } from "@/Api";
import { selectSubscriptionId, selectToken } from "@/auth/authSlice";
import {
    PERMISSION_VIEW_CONFIDENTIAL_DATA,
    PERMISSION_VIEW_RESTRICTED_DATA,
    PERMISSION_EDIT_EXCHANGE_RATES,
} from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";

import Comments from "@/common/Comments";
import ConfirmationDialog from "@/common/ConfirmationDialog";
import { SearchBox } from "@/common/StyledComponents";
import { SOCKET_BASE_URL } from "@/config";
import { currencies } from "@/constants";
import BidActivity from "@/sheet/BidActivity";
import BidBills from "@/sheet/BidBills";
import BidHeader from "@/sheet/BidHeader";
import BidLoading from "@/sheet/BidLoading";
import BidNotes from "@/sheet/BidNotes";
import BidSheetTabs from "@/sheet/BidSheetTabs";
import {
    selectIsUpdating,
    selectOpenCommentKey,
    selectOpenComments,
    setHighlights,
    setOpenCommentKey,
    setOpenComments,
    setSelectedCellCommentKey,
    setUpdating,
} from "@/sheet/bidSlice";
import BidSum from "@/sheet/BidSum";
import { ColumnsMenu } from "@/sheet/ColumnsMenu";
import DynamicRateTemplate from "@/sheet/DynamicRateTemplate";
import HighlightsOverview from "@/sheet/HighlightsOverview";
import {
    getServiceItems,
    internalServiceItems,
} from "@/sheet/serviceItemsStore";
import TopSheetButton from "@/sheet/TopSheetButton";
import { generateRandomColor } from "@/utils";
import { AddonPopup } from "./AddonForm";
import AddonsForm from "./AddonsForm";
import { handleBidChanges } from "./bidChangesHandler";
import { allColumnProfiles, getLeftStickingColumnCount } from "./bidColumns";
import { BidComments } from "./BidComments";

import {
    bidToRows,
    fitColumnWidthsToContainer,
    getReadOnly,
    getRootAddons,
    getStyledRows,
    preProcessColumnProfiles,
    getProcessedColumnProfiles,
    store,
    storeBid,
    getLineItem,
    setExpansion,
    matchRunningExpansionsToLineItems,
    optimisticToggleCleanBid,
} from "./bidStore";

import {
    ADDON_ROW_ID_PREFIX,
    LINE_GROUP_ROW_ID_PREFIX,
    LINE_ITEM_ROW_ID_PREFIX,
    LINE_RUNNING_COLUMN_ID_PREFIX,
    SPACER_ROW_ID_PREFIX,
    rateTypes,
    resourceTypes,
    ROW_CELL_HEIGHT,
    SPACER_ROW_HEIGHT,
} from "./constants";
import CustomChevronCellTemplate from "./CustomChevronCellTemplate";
import CustomDropdownCellTemplate from "./CustomDropdownCellTemplate";
import { CustomNumberCellTemplate } from "./CustomNumberCellTemplate";
import { CustomTextCellTemplate } from "./CustomTextCellTemplate";

import { FormulaCellTemplate } from "./FormulaCellTemplate";
import { tryParseFormula } from "./formulaParser";
import {
    getColumns,
    getSearchedLineItemRows,
    getVisibleRows,
    reorderArray,
    rowsToHashTable,
} from "./sheetUtils";
import { TopSheetConfigurator } from "./TopSheetConfigurator";
import SubQuantityColumnsModal from "./SubQuantityColumnsModal";

const highlightColor = generateRandomColor();

export default function BidSheet({ isPublic }) {
    const { BID_ID } = useParams(); // TODO: use path param
    const theme = useTheme();
    const dispatch = useDispatch();
    const themeMode = theme.palette.mode || "light";
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [running, setRunning] = useState(false);
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);
    const [columnRightStickyCount, setColumnRightStickyCount] = useState(0);
    const [expandAll, setExpandAll] = useState(false);
    const [lastExpandedAt, setLastExpandedAt] = useState(null);
    const [columnMenuOpen, setColumnMenuOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [gridHeaderHeight, setGridHeaderHeight] = useState(60);

    const [addonPopupOpen, setAddonPopupOpen] = useState(false);
    const [addonPopupData, setAddonPopupData] = useState(null);

    const [searchString, setSearchString] = useState("");

    const [quotedTotal, setQuotedTotal] = useState(0);

    const [topConfigOpen, setTopConfigOpen] = useState(false);
    const [requestFormOpen, setRequestFormOpen] = useState(false);
    const [timestampsOpen, setTimestampsOpen] = useState(false);
    const [zeroOutPopupOpen, setZeroOutPopupOpen] = useState(false);
    const [mirrorPopupOpen, setMirrorPopupOpen] = useState(false);
    const [subQuantityModalOpen, setSubQuantityModalOpen] = useState(false);

    const updating = useSelector(selectIsUpdating);
    const openCommentKey = useSelector(selectOpenCommentKey);
    const commentsOpen = useSelector(selectOpenComments);
    const socket = useRef();
    const columnMenuAnchor = useRef();
    const socketConnectionEstablished = useRef(false);
    const token = useSelector(selectToken);
    const { hasPermission } = useEntitlements();
    const canViewConfidentialData = hasPermission(
        PERMISSION_VIEW_CONFIDENTIAL_DATA,
    );
    const canViewRestrictedData = hasPermission(
        PERMISSION_VIEW_RESTRICTED_DATA,
    );
    const canEditExchangeRates = hasPermission(PERMISSION_EDIT_EXCHANGE_RATES);
    const subscriptionId = useSelector(selectSubscriptionId);
    const rowsToRender = getVisibleRows(rows);
    const rowsSearched = getSearchedLineItemRows(rowsToRender, searchString);
    const runningInitialized = store.bid.line_items_running?.length > 0;
    const runningInitializing = running && updating && !runningInitialized;
    const columnLeftStickyCount = getLeftStickingColumnCount();

    const lastScrollTop = useRef(0);
    const reactGridRef = useRef();
    const reactGridHeaderRef = useRef();
    const gridAndSettingRef = useRef();
    const bidHeaderRef = useRef();
    const themeRef = useRef(theme);

    // Keep themeRef in sync so callbacks that outlive a render cycle
    // (e.g. WebSocket handlers) always read the latest theme.
    useEffect(() => {
        themeRef.current = theme;
    }, [theme]);

    const readOnly = getReadOnly();
    const rootAddons = getRootAddons();

    const isTemplate =
        store.bid.project_id === null || store.bid.project_id === undefined;
    const setSumDataRef = useRef();

    const drawerOpen = timestampsOpen || commentsOpen;

    const registerChildSumDataFunction = (func) => {
        setSumDataRef.current = func;
    };

    const sendSocketMessage = (payload) => {
        if (!socket.current || socket.current.readyState !== WebSocket.OPEN) {
            return;
        }

        try {
            socket.current.send(JSON.stringify(payload));
        } catch {
            handleSocketClose();
        }
    };

    const handleSocketOpen = () => {
        socketConnectionEstablished.current = true;
    };

    const handleSocketClose = () => {
        if (socketConnectionEstablished.current) {
            socketConnectionEstablished.current = false;
            createSocket();
        }
    };

    const handleSocketError = () => {
        handleSocketClose();
    };

    const setRowsFromBid = useCallback(() => {
        const bidRows = bidToRows();
        const styledBidRows = getStyledRows(bidRows, themeRef.current);
        setRows(styledBidRows);
    }, []);

    const refreshBid = (bid) => {
        storeBid(bid);
        setQuotedTotal(bid.total_quoted);
        matchRunningExpansionsToLineItems();

        const processedProfiles = getProcessedColumnProfiles();
        setColumns(getColumns(processedProfiles));
        setColumnRightStickyCount(
            processedProfiles.filter((col) => col.sticky).length,
        );

        setRowsFromBid();
        dispatch(setUpdating(false));
    };

    const handleColumnSettingToggle = () => {
        const processedProfiles = getProcessedColumnProfiles();
        setColumns(getColumns(processedProfiles));
        setColumnRightStickyCount(
            processedProfiles.filter((col) => col.sticky).length,
        );
    };

    const handleSocketData = (event) => {
        let payloadParsed;

        try {
            payloadParsed = JSON.parse(event.data);
        } catch {
            return;
        }

        const payloadType = payloadParsed.type;
        const data = payloadParsed.data;

        if (payloadType === "bid") {
            refreshBid(data);
        } else if (payloadType === "highlight") {
            dispatch(setHighlights(data));
        } else if (payloadType === "bid_changes") {
            handleBidChanges(data);
            refreshBid({ ...store.bid });
        } else if (payloadType === "bid_clean") {
            optimisticToggleCleanBid(data);
            refreshBid({ ...store.bid });
        }
    };

    const getBid = () => {
        request
            .get(`/bids${isPublic ? "/public" : ""}/${BID_ID}`)
            .then((response) => {
                storeBid(response.data);
                preProcessColumnProfiles(
                    subscriptionId,
                    canViewRestrictedData,
                    canEditExchangeRates,
                );
                refreshBid(response.data);
                handleColumnSettingToggle();
                handleToggleRunning(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const updateLineItem = (id, field, val, isRunning = false) => {
        const requestedAt = Date.now();
        let payload = { [field]: val };
        let changeData = { id, [field]: val };

        if (field.startsWith("sub_quantities.")) {
            const subKey = field.split(".")[1];
            const currentLine = getLineItem(id, isRunning);
            const newSubQuantities = {
                ...currentLine.sub_quantities,
                [subKey]: val,
            };
            payload = { sub_quantities: newSubQuantities };
            changeData = { id, sub_quantities: newSubQuantities };
        }

        request.patch(
            `bids/${BID_ID}/line-items/${isRunning ? "running/" : ""}${id}?timestamp=${requestedAt}`,
            payload,
        );
        store.lastRequestTimestamp = requestedAt;
        dispatch(setUpdating(true));
        handleBidChanges([
            {
                model_type: isRunning ? "line_item_running" : "line_item",
                change_type: "updated",
                data: changeData,
            },
        ]);
    };

    const updateLineGroup = (id, field, val, isRunning = false) => {
        request.patch(
            `bids/${BID_ID}/line-groups/${isRunning ? "running/" : ""}${id}`,
            { [field]: val },
        );
        dispatch(setUpdating(true));
        handleBidChanges([
            {
                model_type: isRunning ? "line_group_running" : "line_group",
                change_type: "updated",
                data: { id, [field]: val },
            },
        ]);
    };

    const updateAddon = (id, field, val) => {
        const addon = store.bid.addons.find((addon) => addon.id === id);
        if (!addon) {
            return;
        }
        request.put(`bids/${BID_ID}/addon/${id}`, { ...addon, [field]: val });
        dispatch(setUpdating(true));
        handleBidChanges([
            {
                model_type: "addon",
                change_type: "updated",
                data: { id, [field]: val },
            },
        ]);
    };

    const orderChildren = (parentId, childrenIds) => {
        if (parentId) {
            request.put(
                `/bids/${BID_ID}/line-groups/${parentId}/order`,
                childrenIds,
            );
        } else {
            request.put(`/bids/${BID_ID}/line-groups/order`, childrenIds);
        }
        dispatch(setUpdating(true));
    };

    const handleResetRunning = () => {
        request.post(`/bids/${BID_ID}/reset-running`);
        dispatch(setUpdating(true));
    };

    const handleZeroOutRunning = () => {
        request.post(`/bids/${BID_ID}/zero-out-running`);
        dispatch(setUpdating(true));
    };

    const handleCancelRunning = () => {
        request.post(`/bids/${BID_ID}/cancel-running`);
        dispatch(setUpdating(true));
    };

    const handlePageUnmount = () => {
        if (socket.current) {
            socketConnectionEstablished.current = false;
            socket.current.close();
        }
    };

    const handleSelectionChanged = (ranges) => {
        if (!ranges?.length) {
            return;
        }

        // NOTE: why does this event invokes on both mouse up and down
        handleSelectionSummary(ranges);
        handleSelectionHighlight(ranges);

        const selection = ranges[0].first;
        const columnId = selection.column.columnId;
        const rowId = selection.row.rowId;
        dispatch(setSelectedCellCommentKey(`${rowId}-${columnId}`));
    };

    const handleSelectionHighlight = (ranges) => {
        if (!ranges?.length) {
            return;
        }

        const selection = ranges[0].first;
        sendSocketMessage({
            type: "highlight",
            data: {
                rowId: selection.row.rowId,
                columnId: selection.column.columnId,
                borderColor: highlightColor,
            },
        });
    };

    const handleSelectionSummary = (ranges) => {
        if (!ranges?.length) {
            return;
        }

        let selectedSum = 0;
        let selectedCount = 0;
        let selectedNumCount = 0;
        let selectedAverage = 0;
        ranges.forEach((range) => {
            const colIdxStart = range.columns[0].idx;
            const colIdxEnd = range.columns[range.columns.length - 1].idx + 1;
            range.rows.forEach((row) => {
                const selectedCells = row.cells.slice(colIdxStart, colIdxEnd);
                selectedCells.forEach((cell) => {
                    if (!isNaN(cell.value)) {
                        selectedSum += parseFloat(cell.value);
                        selectedNumCount += 1;
                    }
                    selectedCount += 1;
                });
            });
        });
        selectedAverage =
            selectedNumCount === 0 ? 0 : selectedSum / selectedNumCount;
        setSumDataRef.current &&
            setSumDataRef.current({
                selectedSum: selectedSum.toFixed(2),
                selectedCount,
                selectedAverage: selectedAverage.toFixed(2),
            });
    };

    const handleToggleExpandAll = () => {
        const nowExpandAll = !expandAll;
        rows.forEach((row) => {
            if (row.rowId.startsWith("group")) {
                setExpansion(row.rowId, nowExpandAll);
            }
        });
        setExpandAll(nowExpandAll);
        setRowsFromBid();
        handleMenuClose();

        setTimeout(() => {
            setLastExpandedAt(Date.now());
        }, 100);
    };

    const handleColumnToggleVisibility = (id, isVisible) => {
        const profile = allColumnProfiles.find((col) => col.id === id);
        if (!profile) {
            return;
        }

        profile.visible = isVisible;
        fitColumnWidthsToContainer(reactGridRef.current);
        handleColumnSettingToggle();
        setRowsFromBid();

        if (isTemplate) {
            request.patch(`/bids/${BID_ID}/template-bid`, {
                visible_columns: allColumnProfiles
                    .filter((col) => col.visible)
                    .map((col) => col.id),
            });
        }
    };

    const handleColumnToggleSticky = (id, isSticky) => {
        const profile = allColumnProfiles.find((col) => col.id === id);
        if (!profile) {
            return;
        }

        profile.sticky = isSticky;
        handleColumnSettingToggle();
        setRowsFromBid();

        if (isTemplate) {
            request.patch(`/bids/${BID_ID}/template-bid`, {
                sticky_columns: allColumnProfiles
                    .filter((col) => col.sticky)
                    .map((col) => col.id),
            });
        }
    };

    const handleExpandRow = useCallback(
        (groupRowId, expanded) => {
            setExpansion(groupRowId, expanded);
            setRowsFromBid();

            setTimeout(() => {
                setLastExpandedAt(Date.now());
            }, 100);
        },
        [setRowsFromBid],
    );

    const handleSectionLinkClick = (sectionId) => {
        const rowId = `${LINE_GROUP_ROW_ID_PREFIX}-${sectionId}`;
        let y = 0;
        const defaultRowHeight = 25; // see: https://reactgrid.com/docs/4.0/7-api/0-interfaces/2-row/
        for (const row of rowsSearched) {
            if (row.rowId === rowId) {
                break;
            }
            y += row.height ?? defaultRowHeight;
        }
        y = y - ROW_CELL_HEIGHT; // adjust to row top
        if (reactGridRef.current && bidHeaderRef.current) {
            document.documentElement.scrollTo({
                behavior: "smooth",
                top:
                    y +
                    bidHeaderRef.current.clientHeight +
                    bidHeaderRef.current.offsetTop,
            });
        }
        handleExpandRow(rowId, true);
    };

    const handleCommentAction = (comment) => {
        dispatch(setOpenCommentKey(comment.meta.comment_key));
    };

    const handleDeleteRow = useCallback(
        (rowId) => {
            // TODO: implement async waiting feedback
            const [rowTypePrefix, modelId] = rowId.split("-");
            if (rowTypePrefix === ADDON_ROW_ID_PREFIX) {
                request.delete(`bids/${BID_ID}/addon/${modelId}`);
            } else if (rowTypePrefix === LINE_ITEM_ROW_ID_PREFIX) {
                request.delete(
                    `bids/${BID_ID}/line-items${running ? "/running" : ""}/${modelId}`,
                );
            } else if (rowTypePrefix === LINE_GROUP_ROW_ID_PREFIX) {
                request.delete(
                    `bids/${BID_ID}/line-groups${running ? "/running" : ""}/${modelId}`,
                );
            }
            dispatch(setUpdating(true));
        },
        [BID_ID, running, dispatch],
    );

    const handleCopyRow = useCallback(
        (rowId) => {
            // TODO: implement async waiting feedback
            const [rowTypePrefix, modelId] = rowId.split("-");
            if (rowTypePrefix === ADDON_ROW_ID_PREFIX) {
                const addonData = store.bid.addons.find(
                    (addon) => addon.id === modelId,
                );
                request.post(`bids/${BID_ID}/addon`, addonData);
            } else if (rowTypePrefix === LINE_ITEM_ROW_ID_PREFIX) {
                const lineItemData = getLineItem(modelId, running);
                request.post(
                    `bids/${BID_ID}/line-items${running ? "/running" : ""}`,
                    lineItemData,
                );
            } else if (rowTypePrefix === LINE_GROUP_ROW_ID_PREFIX) {
                request.post(
                    `bids/${BID_ID}/line-groups${running ? "/running" : ""}/${modelId}/copy`,
                );
            }
            dispatch(setUpdating(true));
        },
        [BID_ID, running, dispatch],
    );

    const handleCleanBid = () => {
        dispatch(setUpdating(true));
        optimisticToggleCleanBid(true);
        refreshBid(store.bid);
        request.put(`/bids/${BID_ID}/clean`).catch(() => {
            optimisticToggleCleanBid(false);
            refreshBid(store.bid);
        });
        handleMenuClose();
    };

    const handleAddonPopupOpen = useCallback((addonId) => {
        const addon = store.bid.addons.find((addon) => addon.id === addonId);
        if (addon) {
            setAddonPopupOpen(true);
            setAddonPopupData(addon);
        }
    }, []);

    const handleOpenComments = useCallback(() => {
        dispatch(setOpenComments(true));
        setMenuAnchorEl(null);
    }, [dispatch]);

    const handleSearch = (e) => {
        setSearchString(e.target.value || "");
    };

    const handleTopsheetConfigOpen = () => {
        setTopConfigOpen(true);
        handleMenuClose();
    };

    const handleToggleRunning = (newRunningState) => {
        setRunning(newRunningState);
        store.showRunning = newRunningState;
        handleColumnSettingToggle();
        setRowsFromBid();
    };

    const handleTopsheetAndRequestFormOpen = () => {
        setTopConfigOpen(true);
        setRequestFormOpen(true);
    };

    const handleColumnMenuToggle = (open) => (event) => {
        event.stopPropagation();
        setColumnMenuOpen(open);

        if (!open) {
            handleMenuClose();
        }
    };

    const handleScrollToCell = useCallback(
        (rowId, columnId) => {
            const row = rows.find((currRow) => currRow.rowId === rowId);
            if (!row) {
                return;
            }
            const categoryRowId = row?.parentId;
            const categoryRow = rows.find(
                (currRow) => currRow.rowId === categoryRowId,
            );
            const sectionRowId = categoryRow?.parentId;

            if (categoryRowId) {
                handleExpandRow(categoryRowId, true);
            }
            if (sectionRowId) {
                handleExpandRow(sectionRowId, true);
            }

            let y = 0;
            const defaultRowHeight = 25; // see: https://reactgrid.com/docs/4.0/7-api/0-interfaces/2-row/
            for (const row of rowsSearched) {
                if (row.rowId === rowId) {
                    break;
                }
                y += row.height ?? defaultRowHeight;
            }
            y = y - ROW_CELL_HEIGHT; // adjust to row top
            if (reactGridRef.current && bidHeaderRef.current) {
                document.documentElement.scrollTo({
                    top:
                        y +
                        bidHeaderRef.current.clientHeight +
                        bidHeaderRef.current.offsetTop,
                });
            }

            let x = 0;
            for (const column of columns) {
                if (column.columnId === columnId) {
                    break;
                }
                x += column.width;
            }

            if (reactGridRef.current) {
                reactGridRef.current.scrollTo({
                    left: x - 300,
                });
            }
        },
        [columns, handleExpandRow, rows, rowsSearched],
    );

    const customCellTemplates = useMemo(
        () => ({
            simpleFormula: new FormulaCellTemplate(),
            simpleFormulaPercentage: new FormulaCellTemplate(false, true),
            simpleFormulaOneAsEmpty: new FormulaCellTemplate(true),
            customChevron: new CustomChevronCellTemplate(
                handleExpandRow,
                handleCopyRow,
                handleDeleteRow,
                handleAddonPopupOpen,
                lastExpandedAt,
            ),
            dynamicRate: new DynamicRateTemplate(internalServiceItems),
            customDropdownRateType: new CustomDropdownCellTemplate(rateTypes),
            customDropdownCurrency: new CustomDropdownCellTemplate(currencies),
            customDropdownResourceType: new CustomDropdownCellTemplate(
                resourceTypes,
            ),
            customText: new CustomTextCellTemplate(),
            customNumber: new CustomNumberCellTemplate(),
        }),
        [
            handleExpandRow,
            handleCopyRow,
            handleDeleteRow,
            handleAddonPopupOpen,
            lastExpandedAt,
        ],
    );

    const createSocket = () => {
        if (isPublic) {
            return;
        }
        socket.current = new WebSocket(
            `${SOCKET_BASE_URL}/ws/bid/${BID_ID}?token=${token}`,
        );
        socket.current.onclose = handleSocketClose;
        socket.current.onerror = handleSocketError;
        socket.current.onopen = handleSocketOpen;
        socket.current.onmessage = handleSocketData;
    };

    // Keep socket lifecycle tied to auth and bid identity changes.
    useEffect(() => {
        if (isPublic) {
            return undefined;
        }

        socket.current = new WebSocket(
            `${SOCKET_BASE_URL}/ws/bid/${BID_ID}?token=${token}`,
        );
        socket.current.onclose = handleSocketClose;
        socket.current.onerror = handleSocketError;
        socket.current.onopen = handleSocketOpen;
        socket.current.onmessage = handleSocketData;

        return handlePageUnmount;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [BID_ID, isPublic, token]);

    // Initial data bootstrap should run once on mount.
    useEffect(() => {
        getBid();
        getServiceItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!loading) {
            setRowsFromBid();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeMode]);

    useEffect(() => {
        if (reactGridHeaderRef.current) {
            setGridHeaderHeight(reactGridHeaderRef.current.offsetHeight);
        }
    }, [loading]);

    useEffect(() => {
        const handleScroll = () => {
            const g = reactGridRef.current;
            const gs = gridAndSettingRef.current;
            const currentScrollTop = document.documentElement.scrollTop;
            const deltaY = currentScrollTop - lastScrollTop.current;
            lastScrollTop.current = currentScrollTop;

            if (!g || !gs) {
                return;
            }

            const gsRect = gs.getBoundingClientRect();

            if (deltaY > 0) {
                if (gsRect.top <= 0) {
                    g.scrollBy(0, deltaY);
                }
            } else if (deltaY < 0) {
                if (Math.round(gsRect.bottom) === window.innerHeight) {
                    g.scrollBy(0, deltaY);
                } else if (
                    g.scrollTop !== 0 &&
                    gsRect.bottom > window.innerHeight
                ) {
                    g.scrollTo({
                        behavior: "smooth",
                        top: 0,
                    });
                }
            }
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        if (openCommentKey) {
            const rowIdComponents = openCommentKey.split("-");
            const rowId = `${rowIdComponents[0]}-${rowIdComponents[1]}`;
            const columnId = rowIdComponents[2];

            requestAnimationFrame(() => {
                handleScrollToCell(rowId, columnId);
            });
        }
    }, [openCommentKey, handleScrollToCell]);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (event) => {
        if (event) {
            event.stopPropagation();
        }
        setMenuAnchorEl(null);
    };

    const handleCellChanged = (changes) => {
        changes.forEach((change) => {
            localCellChangeHandler(change);
            customCellChangeHandler(change);
        });
    };

    const localCellChangeHandler = ({
        columnId,
        newCell,
        previousCell,
        rowId,
        type,
    }) => {
        if (type === "dropdown") {
            if (previousCell.isOpen !== newCell.isOpen) {
                if (newCell.isOpen) {
                    store.localData.openedDropdownCell.rowId = rowId;
                    store.localData.openedDropdownCell.columnId = columnId;
                } else {
                    store.localData.openedDropdownCell.rowId = null;
                    store.localData.openedDropdownCell.columnId = null;
                }
            }
            setRowsFromBid();
        } else {
            const valueOrTextChanged =
                newCell.text !== previousCell.text ||
                (newCell.value !== previousCell.value && !isNaN(newCell.value));
            if (valueOrTextChanged) {
                const newRows = [...rows];
                const changeRowIdx = rows.findIndex((el) => el.rowId === rowId);
                const changeColumnIdx = columns.findIndex(
                    (el) => el.columnId === columnId,
                );
                newRows[changeRowIdx].cells[changeColumnIdx] = newCell;
                setRows(newRows);
            }
        }
    };

    const customCellChangeHandler = ({
        columnId,
        newCell,
        previousCell,
        rowId,
    }) => {
        const isRunning = columnId.startsWith(LINE_RUNNING_COLUMN_ID_PREFIX);
        let runningColumnId = columnId.replace(
            `${LINE_RUNNING_COLUMN_ID_PREFIX}.`,
            "",
        );

        if (newCell.text !== previousCell.text) {
            const modelType = rowId.split("-")[0];
            const modelId = rowId.split("-")[1];

            const isTextCell =
                newCell.type === "customChevron" ||
                newCell.type === "text" ||
                newCell.type === "dropdown" ||
                newCell.type.includes("customDropdown") ||
                newCell.type === "customText";
            const isNumberCell =
                newCell.type === "simpleFormula" ||
                newCell.type === "number" ||
                newCell.type === "simpleFormulaOneAsEmpty" ||
                newCell.type === "simpleFormulaPercentage";
            const isCheckboxCell = newCell.type === "checkbox";
            const isDynamicRateCell = newCell.type === "dynamicRate";

            let value;

            if (isNumberCell) {
                if (newCell.text.startsWith && newCell.text.startsWith("=")) {
                    newCell.value = tryParseFormula(newCell.text);
                }
                if (newCell.value == previousCell.value) {
                    return;
                }
                value = newCell.value;
            } else if (isTextCell) {
                value = newCell.text;
                if (
                    columnId === "is_internal" ||
                    runningColumnId === "is_internal"
                ) {
                    value = newCell.text === "Internal";
                }
            } else if (isCheckboxCell) {
                value = newCell.text === "1";
            } else if (isDynamicRateCell) {
                columnId = "service_item_id";
                runningColumnId = "service_item_id";
                value = newCell.value.id;
            }

            if (modelType === LINE_ITEM_ROW_ID_PREFIX) {
                updateLineItem(
                    modelId,
                    isRunning ? runningColumnId : columnId,
                    value,
                    isRunning,
                );
            } else if (modelType === LINE_GROUP_ROW_ID_PREFIX) {
                updateLineGroup(
                    modelId,
                    isRunning ? runningColumnId : columnId,
                    value,
                    isRunning,
                );
            } else if (modelType === ADDON_ROW_ID_PREFIX) {
                updateAddon(modelId, columnId, value);
            }
        }
    };

    const customRowsReorderHandler = (targetRowId, rowIds) => {
        if (rowIds.length > 1) return;

        const [sourcePrefix, sourceIdStr] = rowIds[0].split("-");
        const sourceId = parseInt(sourceIdStr, 10);
        if (Number.isNaN(sourceId)) return;

        const isOrderingLine = sourcePrefix === LINE_ITEM_ROW_ID_PREFIX;
        const isOrderingGroup = sourcePrefix === LINE_GROUP_ROW_ID_PREFIX;
        if (!isOrderingLine && !isOrderingGroup) return;

        // Use reorderArray (same function localRowsReorderHandler uses) to
        // compute the new flat row order — single source of truth.
        const fromIdx = rows.findIndex((r) => r.rowId === rowIds[0]);
        const toIdx = rows.findIndex((r) => r.rowId === targetRowId);
        if (fromIdx === -1 || toIdx === -1) return;
        const reorderedRows = reorderArray(rows, [fromIdx], toIdx);

        const lineGroupsHash = Object.fromEntries(
            store.bid.line_groups.map((g) => [g.id, g]),
        );
        const lineItemsHash = isOrderingLine
            ? Object.fromEntries(store.bid.line_items.map((l) => [l.id, l]))
            : {};

        // Determine source parent from data model
        const sourceParentId = isOrderingLine
            ? lineItemsHash[sourceId].line_group_id
            : lineGroupsHash[sourceId].parent_id;

        // Determine target parent from the row indent relationship:
        //   same indent  → sibling/add-button → target's parent is our parent
        //   indent - 1   → group header        → that group IS the parent
        const sourceRow = rows[fromIdx];
        const targetRow = rows[toIdx];
        let targetParentId;
        if (sourceRow.indent === targetRow.indent) {
            targetParentId = targetRow.parentId
                ? parseInt(targetRow.parentId.split("-")[1], 10)
                : null;
        } else if (sourceRow.indent === targetRow.indent + 1) {
            targetParentId = parseInt(targetRowId.split("-")[1], 10);
        } else {
            return;
        }

        const sameParent = sourceParentId === targetParentId;
        const childPrefix = isOrderingLine
            ? LINE_ITEM_ROW_ID_PREFIX
            : LINE_GROUP_ROW_ID_PREFIX;

        // For the moved source, treat it as belonging to the target parent
        const getParent = (id) => {
            if (!sameParent && id === sourceId) return targetParentId;
            return isOrderingLine
                ? lineItemsHash[id]?.line_group_id
                : lineGroupsHash[id]?.parent_id;
        };

        // Extract the new child order from the reordered flat list
        const extractChildOrder = (parentId) =>
            reorderedRows
                .filter((r) => {
                    const [p, idStr] = r.rowId.split("-");
                    if (p !== childPrefix) return false;
                    const id = parseInt(idStr, 10);
                    return getParent(id) === parentId;
                })
                .map((r) => parseInt(r.rowId.split("-")[1], 10));

        const parents = new Set([sourceParentId, targetParentId]);
        for (const parentId of parents) {
            orderChildren(parentId, extractChildOrder(parentId));
        }
    };

    const localRowsReorderHandler = (targetRowId, rowIds) => {
        setRows((prevRows) => {
            const to = prevRows.findIndex((row) => row.rowId === targetRowId);
            const rowsIds = rowIds.map((id) =>
                prevRows.findIndex((row) => row.rowId === id),
            );
            return reorderArray(prevRows, rowsIds, to);
        });
    };

    const handleRowsReorder = (targetRowId, rowIds) => {
        // localRowsReorderHandler(targetRowId, rowIds);
        customRowsReorderHandler(targetRowId, rowIds);
    };

    const handleCanReorderRows = (targetRowId, rowIds) => {
        if (rowIds.length > 1) return false;

        // Block drops on spacer rows
        if (targetRowId.startsWith(SPACER_ROW_ID_PREFIX)) return false;

        const rowsHash = rowsToHashTable(rows);
        const sourceRow = rowsHash[rowIds[0]];
        const targetRow = rowsHash[targetRowId];
        if (!sourceRow || !targetRow) return false;

        // Same indent: sibling or add-button (→ bottom of group)
        // indent - 1:  parent group header   (→ top of group)
        return (
            sourceRow.indent === targetRow.indent ||
            sourceRow.indent === targetRow.indent + 1
        );
    };

    return loading ? (
        <BidLoading />
    ) : (
        <div
            style={{
                width: `calc(100% - ${timestampsOpen ? 332 : commentsOpen ? 400 : 0}px)`,
                transition: "all 0.15s ease-out",
            }}
        >
            <div ref={bidHeaderRef}>
                {store.bid.id && (
                    <BidHeader
                        getBid={getBid}
                        refreshBid={refreshBid}
                        timestampsOpen={timestampsOpen}
                        setTimestampsOpen={setTimestampsOpen}
                        openRequestForm={handleTopsheetAndRequestFormOpen}
                        openTopsheetConfig={handleTopsheetConfigOpen}
                        isRunning={running}
                    />
                )}
            </div>

            <div
                style={{
                    position: "relative",
                    width: "100%",
                    height:
                        rowsSearched.reduce(
                            (sum, row) => sum + (row.height ?? ROW_CELL_HEIGHT),
                            0,
                        ) + gridHeaderHeight,
                }}
            >
                <div
                    ref={gridAndSettingRef}
                    style={{ position: "sticky", top: 0 }}
                >
                    <div
                        ref={reactGridHeaderRef}
                        style={{ paddingTop: isMobile ? "12px" : 0 }}
                    >
                        <BidSheetTabs
                            running={running}
                            runningInitialized={runningInitialized}
                            handleToggleRunning={handleToggleRunning}
                        />

                        <Grid
                            container
                            justifyContent={"space-between"}
                            flexWrap={isMobile ? "wrap" : "nowrap"}
                        >
                            <Grid item paddingBottom={2}>
                                <Grid container spacing={2}>
                                    {isMobile ? (
                                        <Grid item>
                                            <IconButton
                                                onClick={handleMenuOpen}
                                            >
                                                <MenuDots size={18} />
                                            </IconButton>
                                            <Menu
                                                anchorEl={menuAnchorEl}
                                                open={Boolean(menuAnchorEl)}
                                                onClose={handleMenuClose}
                                            >
                                                <MenuItem
                                                    onClick={
                                                        handleToggleExpandAll
                                                    }
                                                >
                                                    <ListItemIcon>
                                                        {expandAll ? (
                                                            <AltArrowUp
                                                                size={18}
                                                            />
                                                        ) : (
                                                            <AltArrowDown
                                                                size={18}
                                                            />
                                                        )}
                                                    </ListItemIcon>
                                                    <ListItemText>{`${!expandAll ? "Expand" : "Collapse"} all`}</ListItemText>
                                                </MenuItem>
                                                {!running && (
                                                    <MenuItem
                                                        disabled={running}
                                                        onClick={handleCleanBid}
                                                    >
                                                        <ListItemIcon>
                                                            <ListCrossMinimalistic
                                                                size={18}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText>
                                                            Clean up empty lines
                                                        </ListItemText>
                                                    </MenuItem>
                                                )}
                                                <MenuItem
                                                    onClick={handleColumnMenuToggle(
                                                        true,
                                                    )}
                                                    ref={columnMenuAnchor}
                                                >
                                                    <ListItemIcon>
                                                        <SidebarMinimalistic
                                                            size={18}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        Columns
                                                    </ListItemText>
                                                    <ColumnsMenu
                                                        isOpen={columnMenuOpen}
                                                        close={handleColumnMenuToggle(
                                                            false,
                                                        )}
                                                        anchorEl={
                                                            columnMenuAnchor.current
                                                        }
                                                        onToggleSticky={
                                                            handleColumnToggleSticky
                                                        }
                                                        onToggleVisibility={
                                                            handleColumnToggleVisibility
                                                        }
                                                    />
                                                </MenuItem>
                                                {running ? null : (
                                                    <MenuItem
                                                        onClick={
                                                            handleTopsheetConfigOpen
                                                        }
                                                    >
                                                        <ListItemIcon>
                                                            {readOnly ? (
                                                                <Lock
                                                                    size={18}
                                                                />
                                                            ) : (
                                                                <Tuning
                                                                    size={18}
                                                                />
                                                            )}
                                                        </ListItemIcon>
                                                        <ListItemText>
                                                            Top Sheet
                                                        </ListItemText>
                                                    </MenuItem>
                                                )}
                                                {!isTemplate && (
                                                    <MenuItem
                                                        onClick={() => {
                                                            setMirrorPopupOpen(
                                                                true,
                                                            );
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            {
                                                                <Mirror
                                                                    size={18}
                                                                />
                                                            }
                                                        </ListItemIcon>
                                                        <ListItemText>
                                                            Mirror Quoted
                                                        </ListItemText>
                                                    </MenuItem>
                                                )}
                                                {!isTemplate && (
                                                    <MenuItem
                                                        onClick={() => {
                                                            setZeroOutPopupOpen(
                                                                true,
                                                            );
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            {
                                                                <Eraser
                                                                    size={18}
                                                                />
                                                            }
                                                        </ListItemIcon>
                                                        <ListItemText>
                                                            Clear Running
                                                        </ListItemText>
                                                    </MenuItem>
                                                )}
                                                {!isTemplate && (
                                                    <MenuItem
                                                        onClick={
                                                            handleOpenComments
                                                        }
                                                    >
                                                        <ListItemIcon>
                                                            {
                                                                <ChatDots
                                                                    size={18}
                                                                />
                                                            }
                                                        </ListItemIcon>
                                                        <ListItemText>
                                                            Comments
                                                        </ListItemText>
                                                    </MenuItem>
                                                )}
                                            </Menu>
                                        </Grid>
                                    ) : (
                                        <Grid item>
                                            <Grid
                                                container
                                                alignItems={"center"}
                                                spacing={2}
                                                flexWrap="nowrap"
                                            >
                                                <Grid item>
                                                    <Button
                                                        endIcon={
                                                            expandAll ? (
                                                                <AltArrowUp />
                                                            ) : (
                                                                <AltArrowDown />
                                                            )
                                                        }
                                                        onClick={
                                                            handleToggleExpandAll
                                                        }
                                                        color="secondary"
                                                        size="small"
                                                        variant="contained"
                                                    >
                                                        {`${!expandAll ? "Expand" : "Collapse"} all`}
                                                    </Button>
                                                </Grid>
                                                {!running && (
                                                    <Grid item>
                                                        <Button
                                                            disabled={running}
                                                            onClick={
                                                                handleCleanBid
                                                            }
                                                            endIcon={
                                                                <ListCrossMinimalistic />
                                                            }
                                                            color="secondary"
                                                            size="small"
                                                            variant="contained"
                                                        >
                                                            Clean up empty lines
                                                        </Button>
                                                    </Grid>
                                                )}
                                                {isTemplate && (
                                                    <Grid item>
                                                        <Button
                                                            onClick={() =>
                                                                setSubQuantityModalOpen(
                                                                    true,
                                                                )
                                                            }
                                                            endIcon={<Cart2 />}
                                                            color="secondary"
                                                            size="small"
                                                            variant="contained"
                                                        >
                                                            Subquantities
                                                        </Button>
                                                    </Grid>
                                                )}
                                                <Grid item>
                                                    <Button
                                                        onClick={() => {
                                                            setColumnMenuOpen(
                                                                true,
                                                            );
                                                        }}
                                                        ref={columnMenuAnchor}
                                                        endIcon={
                                                            <SidebarMinimalistic />
                                                        }
                                                        color="secondary"
                                                        size="small"
                                                        variant="contained"
                                                    >
                                                        Columns
                                                    </Button>
                                                    <ColumnsMenu
                                                        isOpen={columnMenuOpen}
                                                        close={() => {
                                                            setColumnMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        anchorEl={
                                                            columnMenuAnchor.current
                                                        }
                                                        onToggleSticky={
                                                            handleColumnToggleSticky
                                                        }
                                                        onToggleVisibility={
                                                            handleColumnToggleVisibility
                                                        }
                                                    />
                                                </Grid>
                                                {running ? null : (
                                                    <Grid item>
                                                        <TopSheetButton
                                                            onOpen={
                                                                handleTopsheetConfigOpen
                                                            }
                                                            variant="endIcon"
                                                        />
                                                    </Grid>
                                                )}
                                                {running && !isTemplate ? (
                                                    <Grid item>
                                                        <Button
                                                            onClick={() => {
                                                                setMirrorPopupOpen(
                                                                    true,
                                                                );
                                                            }}
                                                            endIcon={<Mirror />}
                                                            color="secondary"
                                                            size="small"
                                                            variant="contained"
                                                        >
                                                            Mirror Quoted
                                                        </Button>
                                                    </Grid>
                                                ) : null}
                                                {running && !isTemplate ? (
                                                    <Grid item>
                                                        <Button
                                                            onClick={() => {
                                                                setZeroOutPopupOpen(
                                                                    true,
                                                                );
                                                            }}
                                                            endIcon={<Eraser />}
                                                            color="secondary"
                                                            size="small"
                                                            variant="contained"
                                                        >
                                                            Clear Running
                                                        </Button>
                                                    </Grid>
                                                ) : null}
                                                {running && !isTemplate ? (
                                                    <Grid item>
                                                        <Button
                                                            onClick={
                                                                handleCancelRunning
                                                            }
                                                            endIcon={
                                                                <CloseCircle />
                                                            }
                                                            color="secondary"
                                                            size="small"
                                                            variant="contained"
                                                        >
                                                            Cancel Running
                                                        </Button>
                                                    </Grid>
                                                ) : null}
                                                <Grid item>
                                                    <Button
                                                        onClick={
                                                            handleOpenComments
                                                        }
                                                        endIcon={<ChatDots />}
                                                        color="secondary"
                                                        size="small"
                                                        variant="contained"
                                                    >
                                                        Comments
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}
                                    {updating && (
                                        <Grid item>
                                            <CircularProgress size={20} />
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                            {!drawerOpen && (
                                <Grid item flexGrow={isMobile ? 1 : 0}>
                                    <Grid
                                        container
                                        spacing={1}
                                        alignItems={"center"}
                                    >
                                        <Grid item>
                                            <HighlightsOverview />
                                        </Grid>
                                        <Grid item flexGrow={isMobile ? 1 : 0}>
                                            <SearchBox
                                                handleSearch={handleSearch}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </div>
                    <div
                        id={`react-grid-${themeMode}`}
                        ref={reactGridRef}
                        style={{
                            width: "100%",
                            maxHeight: `calc(100vh - ${gridHeaderHeight}px)`, // position: "sticky",
                            overflow: "-moz-scrollbars-horizontal",
                            overflowX: "scroll",
                            overflowY: "hidden",
                            marginBottom: 0,
                            top: 0,
                        }}
                    >
                        {runningInitializing ? (
                            <CircularProgress />
                        ) : (
                            <ReactGrid
                                rows={rowsSearched}
                                columns={columns}
                                onCellsChanged={handleCellChanged}
                                onRowsReordered={handleRowsReorder}
                                canReorderRows={handleCanReorderRows}
                                onSelectionChanged={handleSelectionChanged}
                                enableRowSelection
                                stickyLeftColumns={columnLeftStickyCount}
                                stickyRightColumns={columnRightStickyCount}
                                horizontalStickyBreakpoint={100}
                                stickyTopRows={1}
                                enableRangeSelection
                                customCellTemplates={customCellTemplates}
                                minRowHeight={SPACER_ROW_HEIGHT}
                            />
                        )}
                    </div>
                </div>
            </div>

            <div>
                {canViewConfidentialData && store.bid.id && (
                    <div>
                        <AddonsForm addonsData={rootAddons} />
                        <BidNotes bidId={BID_ID} readOnly={readOnly} />
                        <Divider sx={{ marginTop: 6, marginBottom: 2 }} />
                        {!isTemplate && (
                            <BidBills
                                bidId={BID_ID}
                                quoted={quotedTotal}
                                legacy={store.bid.project.legacy_bills}
                            />
                        )}
                        {!isTemplate && (
                            <Grid
                                container
                                spacing={8}
                                sx={{ marginBottom: 6 }}
                            >
                                {canViewConfidentialData ? (
                                    <Grid item md={8} flexGrow={1}>
                                        <Comments
                                            comments={store.bid.comments}
                                            modelType={"bid"}
                                            modelInstanceId={BID_ID}
                                            onCommentAction={
                                                handleCommentAction
                                            }
                                        />
                                    </Grid>
                                ) : null}
                                <Grid item md={4} flexGrow={1}>
                                    {store.bid.activity.map((activity) => (
                                        <BidActivity
                                            key={activity.id}
                                            {...activity}
                                        />
                                    ))}
                                </Grid>
                            </Grid>
                        )}
                    </div>
                )}
            </div>

            {store.bid.id && (
                <BidSum
                    isRunning={running}
                    registerSumDataFunction={registerChildSumDataFunction}
                    isTemplate={false}
                    onSectionLinkClick={handleSectionLinkClick}
                />
            )}

            <AddonPopup
                isOpen={addonPopupOpen}
                addonData={addonPopupData}
                lineGroupId={addonPopupData?.line_group_id}
                close={() => {
                    setAddonPopupOpen(false);
                }}
            />

            <TopSheetConfigurator
                open={topConfigOpen}
                close={() => {
                    setTopConfigOpen(false);
                }}
                requestFormOpen={requestFormOpen}
                setRequestFormOpen={setRequestFormOpen}
            />
            <ConfirmationDialog
                title={"Clearing running bid"}
                open={zeroOutPopupOpen}
                closeDialog={() => setZeroOutPopupOpen(false)}
                callback={() => {
                    handleZeroOutRunning();
                    setZeroOutPopupOpen(false);
                }}
            >
                <p>
                    Are you sure? This will zero out your values in the running
                    bid.
                </p>
            </ConfirmationDialog>
            <ConfirmationDialog
                title={"Mirroring running bid"}
                open={mirrorPopupOpen}
                closeDialog={() => setMirrorPopupOpen(false)}
                callback={() => {
                    handleResetRunning();
                    setMirrorPopupOpen(false);
                }}
            >
                <p>
                    Are you sure? This will override your running bid values
                    with ones from the quoted bid.
                </p>
            </ConfirmationDialog>

            <SubQuantityColumnsModal
                isOpen={subQuantityModalOpen}
                onClose={() => setSubQuantityModalOpen(false)}
                bidId={BID_ID}
                refreshBid={refreshBid}
            />

            <BidComments setRunning={handleToggleRunning} />
        </div>
    );
}
